import axios from 'axios';
import { API_BASE } from './config';

/**
 * Fetch monitored addresses for a specific blockchain
 * @param blockchain The blockchain to get monitored addresses for
 * @returns List of monitored addresses
 */
export const fetchMonitoredAddresses = async (blockchain: string) => {
  try {
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    console.log(`Fetching monitored addresses for ${blockchain}`);

    const response = await axios.get(`${API_BASE}/indexer/${blockchain}/addresses`, {
      headers: { 'Cache-Control': 'no-cache' },
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    // Log the response for debugging
    console.log(`Response for ${blockchain} addresses:`, response.data);

    // If the API returns an empty response or error, return a default structure
    if (!response.data || (typeof response.data === 'object' && Object.keys(response.data).length === 0)) {
      console.warn(`Empty response for ${blockchain} addresses, returning default structure`);
      return { addresses: [] };
    }

    // If the API returns an array directly, wrap it in an object
    if (Array.isArray(response.data)) {
      console.log(`Converting array response to object for ${blockchain} addresses`);
      return { addresses: response.data };
    }

    // Return the response data as is
    return response.data;
  } catch (error) {
    console.error('Error fetching monitored addresses:', error);
    // Return an empty addresses array for production
    console.warn(`Error fetching addresses for ${blockchain}, returning empty array`);
    return { addresses: [] };
  }
};

/**
 * Fetch status of all blockchain indexers
 * @param forceRefresh Parameter kept for backward compatibility but no longer used
 * @returns Status information for all indexers
 */
export const fetchIndexerStatus = async (forceRefresh = true) => { // Parameter kept for backward compatibility
  try {
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // Increase timeout to 10 seconds

    console.log('Fetching indexer status (always bypasses cache regardless of forceRefresh parameter)');

    const response = await axios.get(`${API_BASE}/indexer/status`, {
      headers: { 'Cache-Control': 'no-cache' },
      // We still pass the parameter for backward compatibility, but the server ignores it
      params: { force_refresh: true },
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    // Check if the response includes process information
    const data = response.data;

    // Fetch health status for each indexer
    try {
      const healthResponse = await axios.get(`${API_BASE}/indexer/health-status`, {
        headers: { 'Cache-Control': 'no-cache' },
        signal: controller.signal
      });

      const healthData = healthResponse.data;

      // Return the response with proper typing and health status
      return {
        ...data,
        // Ensure these fields exist even if not provided by the API
        processInfo: data.processInfo || undefined,
        processStatus: data.processStatus || undefined,
        processError: data.processError || undefined,
        // Ensure data array has metrics field for each item and add health status
        data: Array.isArray(data.data) ? data.data.map((item: any) => {
          // Find health status for this blockchain
          const healthStatus = healthData?.indexers?.[item.blockchain.toLowerCase()] || null;

          return {
            ...item,
            // Add health status information if available
            healthStatus: healthStatus ? {
              status: healthStatus.status || 'unknown',
              successRate: healthStatus.successRate,
              lastUpdated: healthStatus.lastUpdated,
              recentErrors: healthStatus.recentErrors || [],
              apiMetrics: healthStatus.apiMetrics || {},
              consecutiveFailures: healthStatus.consecutiveFailures || 0
            } : undefined,
            metrics: item.metrics || {
              blocksProcessed: 0,
              transactionsProcessed: 0,
              depositsDetected: 0,
              processingRate: 0,
              lastHourDeposits: 0,
              lastDayDeposits: 0,
              totalDepositAmount: '0',
              lastHourDepositAmount: '0',
              lastDayDepositAmount: '0',
              avgConfirmationTime: 0,
              avgProcessingTime: 0,
              successRate: 100,
              lastMetricsUpdate: Date.now()
            }
          };
        }) : data.data
      };
    } catch (healthError) {
      console.error('Failed to fetch health status:', healthError);

      // Return the response without health status
      return {
        ...data,
        // Ensure these fields exist even if not provided by the API
        processInfo: data.processInfo || undefined,
        processStatus: data.processStatus || undefined,
        processError: data.processError || undefined,
        // Ensure data array has metrics field for each item
        data: Array.isArray(data.data) ? data.data.map((item: any) => ({
          ...item,
          metrics: item.metrics || {
            blocksProcessed: 0,
            transactionsProcessed: 0,
            depositsDetected: 0,
            processingRate: 0,
            lastHourDeposits: 0,
            lastDayDeposits: 0,
            totalDepositAmount: '0',
            lastHourDepositAmount: '0',
            lastDayDepositAmount: '0',
            avgConfirmationTime: 0,
            avgProcessingTime: 0,
            successRate: 100,
            lastMetricsUpdate: Date.now()
          }
        })) : data.data
      }
    };
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.error('Indexer status request timed out');
      // Return a default response instead of throwing
      return {
        status: 'error',
        message: 'Request timed out',
        data: [],
        processStatus: 'error',
        processError: 'Request timed out'
      };
    }
    throw error;
  }
};

/**
 * Fetch configuration of all blockchain indexers
 * @returns Configuration information for all indexers
 */
export const fetchIndexerConfig = async () => {
  try {
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    const response = await axios.get(`${API_BASE}/indexer/config`, {
      headers: { 'Cache-Control': 'no-cache' },
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    return response.data;
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.error('Indexer config request timed out');
      // Return a default response instead of throwing
      return { status: 'error', message: 'Request timed out', data: [] };
    }
    throw error;
  }
};

/**
 * Start all blockchain indexers
 * @returns Result of the operation
 */
export const startAllIndexers = async () => {
  const response = await axios.post(`${API_BASE}/indexer/start`);

  // Force refresh the status after starting all indexers
  try {
    await fetchIndexerStatus(true);
  } catch (error) {
    console.error('Failed to refresh indexer status after starting all indexers:', error);
  }

  return response.data;
};

/**
 * Stop all blockchain indexers
 * @returns Result of the operation
 */
export const stopAllIndexers = async () => {
  const response = await axios.post(`${API_BASE}/indexer/stop`);

  // Force refresh the status after stopping all indexers
  try {
    await fetchIndexerStatus(true);
  } catch (error) {
    console.error('Failed to refresh indexer status after stopping all indexers:', error);
  }

  return response.data;
};

/**
 * Start a specific blockchain indexer
 * @param blockchain Blockchain name (e.g., 'ethereum', 'bitcoin')
 * @returns Result of the operation
 */
export const startIndexer = async (blockchain: string) => {
  const response = await axios.post(`${API_BASE}/indexer/${blockchain}/start`);

  // Force refresh the status after starting the indexer
  try {
    await fetchIndexerStatus(true);
  } catch (error) {
    console.error(`Failed to refresh indexer status after starting ${blockchain} indexer:`, error);
  }

  return response.data;
};

/**
 * Stop a specific blockchain indexer
 * @param blockchain Blockchain name (e.g., 'ethereum', 'bitcoin')
 * @returns Result of the operation
 */
export const stopIndexer = async (blockchain: string) => {
  const response = await axios.post(`${API_BASE}/indexer/${blockchain}/stop`);

  // Force refresh the status after stopping the indexer
  try {
    await fetchIndexerStatus(true);
  } catch (error) {
    console.error(`Failed to refresh indexer status after stopping ${blockchain} indexer:`, error);
  }

  return response.data;
};

/**
 * Update configuration for a blockchain indexer
 * @param config Configuration object
 * @returns Result of the operation
 */
export const updateIndexerConfig = async (config: any) => {
  const response = await axios.post(`${API_BASE}/indexer/config`, config);

  // Force refresh the status after updating the config
  try {
    await fetchIndexerStatus(true);
    await fetchIndexerConfig();
  } catch (error) {
    console.error('Failed to refresh indexer status/config after updating config:', error);
  }

  return response.data;
};

/**
 * Fetch deposit detection metrics
 * @returns Metrics for deposit detection
 */
export const fetchDepositDetectionMetrics = async () => {
  try {
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    const response = await axios.get(`${API_BASE}/indexer/metrics/deposits`, {
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    return response.data;
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.error('Deposit metrics request timed out');
      // Return a default response instead of throwing
      return {
        status: 'error',
        message: 'Request timed out',
        totalDeposits: 0,
        totalAmount: '0',
        depositsLast24h: 0,
        amountLast24h: '0',
        depositsLast7d: 0,
        amountLast7d: '0',
        avgConfirmationTime: 0,
        avgProcessingTime: 0,
        byBlockchain: []
      };
    }
    throw error;
  }
};

/**
 * Fetch recent deposit transactions
 * @param limit Maximum number of transactions to return
 * @param offset Offset for pagination
 * @returns Recent deposit transactions
 */
export const fetchRecentDeposits = async (limit = 10, offset = 0) => {
  try {
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    const response = await axios.get(`${API_BASE}/indexer/deposits/recent`, {
      params: { limit, offset },
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    return response.data;
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.error('Recent deposits request timed out');
      // Return a default response instead of throwing
      return {
        status: 'success',
        data: [],
        total: 0,
        limit,
        offset
      };
    }
    throw error;
  }
};

/**
 * Fetch deposit detection statistics by blockchain
 * @returns Statistics grouped by blockchain
 */
export const fetchDepositStatsByBlockchain = async () => {
  try {
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    const response = await axios.get(`${API_BASE}/indexer/metrics/deposits/by-blockchain`, {
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    return response.data;
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.error('Deposit stats by blockchain request timed out');
      // Return a default response instead of throwing
      return {
        status: 'success',
        blockchains: []
      };
    }
    throw error;
  }
};

/**
 * Reload addresses for a specific blockchain
 * @param blockchain Blockchain name (e.g., 'ethereum', 'bitcoin')
 * @returns Result of the operation
 */
export const reloadAddresses = async (blockchain: string) => {
  try {
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    console.log(`Reloading addresses for ${blockchain}`);

    const response = await axios.post(`${API_BASE}/indexer/${blockchain}/reload-addresses`, {}, {
      headers: { 'Cache-Control': 'no-cache' },
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    // Force refresh the monitored addresses after reloading
    try {
      await fetchMonitoredAddresses(blockchain);
    } catch (error) {
      console.error(`Failed to refresh monitored addresses after reloading for ${blockchain}:`, error);
    }

    return response.data;
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.error('Address reload request timed out');
      // Return a default response instead of throwing
      return {
        status: 'error',
        message: 'Request timed out'
      };
    }
    throw error;
  }
};

/**
 * Fetch Bloom filter configuration
 * @returns Bloom filter configuration
 */
export const fetchBloomFilterConfig = async () => {
  try {
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    console.log('Fetching Bloom filter configuration');

    const response = await axios.get(`${API_BASE}/indexer/bloom-filter-config`, {
      headers: { 'Cache-Control': 'no-cache' },
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    return response.data;
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.error('Bloom filter config request timed out');
      // Return a default response instead of throwing
      return {
        status: 'error',
        message: 'Request timed out',
        config: {
          bloomFilterThreshold: 50,
          bloomFilterRebuildThreshold: 100,
          addressReloadEnabled: true,
          addressReloadInterval: 300000,
          metrics: {
            addressReloads: 0,
            newAddressesFound: 0,
            bloomFilterRebuilds: 0,
            lastReloadTime: null,
            reloadErrors: 0
          }
        }
      };
    }
    throw error;
  }
};

/**
 * Update Bloom filter configuration
 * @param config Configuration object with bloomFilterThreshold and/or bloomFilterRebuildThreshold
 * @returns Result of the operation
 */
export const updateBloomFilterConfig = async (config: {
  bloomFilterThreshold?: number;
  bloomFilterRebuildThreshold?: number;
}) => {
  try {
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    console.log('Updating Bloom filter configuration', config);

    const response = await axios.post(`${API_BASE}/indexer/bloom-filter-config`, config, {
      headers: { 'Cache-Control': 'no-cache' },
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    // Force refresh the configuration after updating
    try {
      await fetchBloomFilterConfig();
    } catch (error) {
      console.error('Failed to refresh Bloom filter configuration after updating:', error);
    }

    return response.data;
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.error('Update Bloom filter config request timed out');
      // Return a default response instead of throwing
      return {
        status: 'error',
        message: 'Request timed out'
      };
    }
    throw error;
  }
};

/**
 * Reprocess a specific block for a blockchain
 * @param blockchain Blockchain name (e.g., 'ethereum', 'bitcoin')
 * @param blockNumber Block number to reprocess
 * @returns Result of the operation with transaction details
 */
export const reprocessBlock = async (blockchain: string, blockNumber: number) => {
  try {
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    console.log(`Reprocessing block ${blockNumber} for ${blockchain}`);

    const response = await axios.post(`${API_BASE}/indexer/${blockchain}/reprocess-block`, {
      blockNumber
    }, {
      headers: { 'Cache-Control': 'no-cache' },
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    return response.data;
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.error('Block reprocessing request timed out');
      // Return a default response instead of throwing
      return {
        status: 'error',
        message: 'Request timed out'
      };
    }
    throw error;
  }
};
